/**
 * Settings for one machine, calm rows: what it allows from the console and the phone (with the step-up said in
 * words), its updates, the policy it follows (a link to that policy), its security posture in words, and for
 * administrators a Details disclosure with the identifiers, and inside it, folded again, the raw Control API.
 */

import { SETTINGS_DECLARATIONS_DIGEST } from '@aioe/shared';
import { Code } from '@gravity-ui/icons';
import { useState } from 'react';

import { routes } from '../../api/client.ts';
import { MachineAllows } from '../../components/machineAllows.tsx';
import { parsePolicyRevision, PolicyLine } from '../../components/machineWords.tsx';
import { ScopeRequestWaiting } from '../../components/scopeRequest.tsx';
import { meshLineOf, postureInWords, postureWeak } from '../../components/meshWords.ts';
import { NodeManage } from '../../components/nodeManage.tsx';
import { DeviceOrders } from '../../components/nodeOrdersPanel.tsx';
import { Button, inputClass, ListGroup, ListRow, StatusMark } from '../../components/ui.tsx';
import { DeviceUpdates } from '../../components/updates.tsx';
import { isCloud } from '../../edition.ts';
import { timeAgo, useLoad } from '../../hooks.ts';
import { useMachine } from '../machineContext.ts';
import type { Workbench } from '../useWorkbench.ts';
import { RemoteView } from './RemoteView.tsx';

export function SettingsView({ w, admin, screen, relay }: { w: Workbench; admin: boolean; screen: boolean; relay: boolean }) {
  const m = useMachine();
  const d = w.device.data;
  const enterprise = !isCloud();
  // Pushed updates are an Enterprise administrator's tool; Cloud machines follow the stable feed.
  const orders = useLoad(() => (enterprise && admin ? routes.updateOrders().catch(() => []) : Promise.resolve([])), [enterprise, admin], 60_000, { whenVisible: true });
  const overview = useLoad(() => (enterprise && admin ? routes.releases().catch(() => undefined) : Promise.resolve(undefined)), [enterprise, admin]);
  const posture = postureInWords(d?.posture);

  return (
    <RemoteView narrow>
      {relay ? (
        <>
          {/* The machine's own ask to be allowed more (it cannot allow itself): decided here or from the Inbox. */}
          <ScopeRequestWaiting deviceId={w.deviceId} onDecided={m.scopes.reload} />
          <ListGroup title="What this machine allows" description="From the console and the phone app. Its own person still decides on the machine.">
            <MachineAllows s={m.scopes} kind={m.kind} screen={screen} />
          </ListGroup>
        </>
      ) : null}

      {d && enterprise && overview.data ? (
        <ListGroup title="Updates" description="Update it now, or roll it back to an earlier release.">
          <DeviceUpdates device={d} orders={orders.data ?? []} overview={overview.data} reload={() => { orders.reload(); w.device.reload(); }} />
        </ListGroup>
      ) : d ? (
        <ListGroup title="Updates">
          <ListRow title={`${d.kind === 'node' ? 'Node' : 'AI Workbench'} ${d.appVersion}`} description={enterprise ? 'Updates follow the release rings your organisation sets.' : 'It updates itself from the stable releases.'} />
        </ListGroup>
      ) : null}

      {d?.kind === 'node' ? <NodeManage device={{ deviceId: d.deviceId, hostname: d.hostname, online: !!d.online, lastSeenAt: d.lastSeenAt, name: d.capabilities?.name }} /> : null}
      {d ? <DeviceOrders device={d} onChange={w.device.reload} /> : null}

      <ListGroup title="Policy and security">
        <ListRow
          title="Policy"
          description={
            <>
              <PolicyLine revision={d?.policyRevision} admin={admin} />
              {d?.policyUnknownKeys?.length ? <span className="block">This version is too old for part of it; updating applies the rest.</span> : null}
            </>
          }
        />
        <ListRow
          title="Security"
          description={`${posture[0]!.toUpperCase()}${posture.slice(1)}${d?.posture?.measuredAt ? `, measured ${timeAgo(d.posture.measuredAt)}` : ''}.`}
          status={postureWeak(d?.posture) ? <StatusMark state="medium" label="Needs attention" /> : <StatusMark state={d?.posture ? 'connected' : 'idle'} label={d?.posture ? 'Reported' : 'Unknown'} />}
        />
        {d?.keyBound ? <ListRow title="Bound to this machine" description="Its access works only with a key that stays on this machine." /> : null}
      </ListGroup>

      {admin && d ? <Details w={w} /> : null}
    </RemoteView>
  );
}

/** For administrators: the identifiers and digests, wrapping rather than overflowing, and the raw Control API folded inside. */
function Details({ w }: { w: Workbench }) {
  const d = w.device.data!;
  const meshNodes = useLoad(() => (isCloud() ? Promise.resolve([]) : routes.meshNodes().catch(() => [])), [d.deviceId]);
  const p = parsePolicyRevision(d.policyRevision);
  const rows: Array<[string, string]> = [
    ['Device id', d.deviceId],
    ['Registered', timeAgo(d.registeredAt)],
    ['Policy', d.policyRevision ? `${p ? `${p.id}, revision ${p.revision}` : ''} · ${d.policyRevision}` : 'not fetched yet'],
    ['Settings declarations', d.settingsDigest ? `${d.settingsDigest.slice(0, 12)} · ${d.manifest ? 'from its policy manifest' : d.settingsDigest === SETTINGS_DECLARATIONS_DIGEST ? "the platform's bundled copy" : 'a build the platform learns from its manifest'}` : 'not reported'],
    ['Policy manifest', d.manifest ? `${d.manifest.version} · ${d.manifest.digest.slice(0, 12)}` : 'not reported (a build from before manifests)'],
    ...(d.policyUnknownKeys?.length ? ([['Too old for', d.policyUnknownKeys.join(', ')]] as Array<[string, string]>) : []),
    ...(!isCloud() ? ([['Private network', meshNodes.data ? meshLineOf(d, meshNodes.data) : '—']] as Array<[string, string]>) : []),
  ];
  return (
    <details className="list-group group">
      <summary className="flex cursor-[var(--cursor-interactive)] list-none items-center gap-2 text-sm font-medium [&::-webkit-details-marker]:hidden">
        Details
        <span className="ms-auto text-xs font-normal text-muted">For administrators</span>
      </summary>
      <div className="grid gap-4">
        <dl className="m-0 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[minmax(9rem,auto)_minmax(0,1fr)]">
          {rows.map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-xs text-muted sm:text-sm">{k}</dt>
              <dd className="mono m-0 min-w-0 break-all text-xs">{v}</dd>
            </div>
          ))}
        </dl>
        <Advanced w={w} />
      </div>
    </details>
  );
}

/** Any Control API method, as the desktop app sends it: for an administrator chasing a problem with support. */
function Advanced({ w }: { w: Workbench }) {
  const [method, setMethod] = useState('workbench.status');
  const [params, setParams] = useState('');
  const [raw, setRaw] = useState<string>();
  const [problem, setProblem] = useState<string>();
  const run = async () => {
    setProblem(undefined);
    let p: unknown = undefined;
    if (params.trim()) {
      try {
        p = JSON.parse(params);
      } catch (e) {
        setProblem(`The parameters are not JSON: ${(e as Error).message}`);
        return;
      }
    }
    try {
      setRaw(JSON.stringify(await routes.rpc(w.deviceId, method, p, w.workspace), null, 2));
    } catch (e) {
      setProblem((e as Error).message);
    }
  };
  return (
    <details className="rounded-lg bg-surface-secondary">
      <summary className="flex cursor-[var(--cursor-interactive)] list-none items-center gap-2 px-3 py-2 text-sm [&::-webkit-details-marker]:hidden">
        <Code className="size-4 text-muted" aria-hidden="true" />
        Advanced
        <span className="ms-auto hidden text-xs text-muted sm:inline">Call the machine directly</span>
      </summary>
      <div className="grid gap-2 px-3 pb-3">
        <div className="flex flex-col gap-2 sm:flex-row">
          <input className={`${inputClass} mono sm:w-56`} value={method} onChange={(e) => setMethod(e.target.value)} placeholder="workbench.status" aria-label="Method" />
          <input className={`${inputClass} mono min-w-0 flex-1`} value={params} onChange={(e) => setParams(e.target.value)} placeholder="Parameters as JSON" aria-label="Parameters" />
          <Button size="sm" variant="secondary" isDisabled={!w.connected} onPress={() => void run()}>
            Call
          </Button>
        </div>
        {problem ? <p className="m-0 text-xs text-danger">{problem}</p> : null}
        {raw ? <pre className="mono m-0 max-h-80 overflow-auto whitespace-pre-wrap break-all rounded-lg bg-background p-3 text-xs">{raw}</pre> : null}
      </div>
    </details>
  );
}
